import React, { useState, useCallback, useMemo } from 'react';
import { generateClient } from 'aws-amplify/api';
import { createCharacter } from '../graphql/mutations';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Sparkles, Book, Shield, Sword, Scroll,
  Heart, Star, User, RefreshCw, ChevronRight, 
  ChevronLeft, Dices, Info, AlertCircle, Feather
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { gameRuleService } from '../services/gameRuleService';
import './CharacterCreation.css';

const NAME_GENERATION_QUERY = `
  query GenerateCharacterName($input: CharacterNameInput!) {
    generateCharacterName(input: $input) {
      name
    }
  }
`;

const BACKSTORY_GENERATION_QUERY = `
  query GenerateBackstory($input: BackstoryInput!) {
    generateBackstory(input: $input) {
      narrative
    }
  }
`;

const CREATION_STEPS = [
  {
    id: 'basics',
    title: 'Character Essence',
    icon: Book,
    description: 'Begin your legend by defining who you are',
    validateFn: (char) => char.name && char.characterClass && char.race && char.alignment
  },
  {
    id: 'abilities',
    title: 'Mystical Powers',
    icon: Sword,
    description: 'Channel your inner strength and abilities',
    validateFn: (char) => Object.values(char.abilities).some(v => v !== 10)
  },
  {
    id: 'background',
    title: 'Sacred History',
    icon: Scroll,
    description: 'Weave the tale of your origins',
    validateFn: (char) => char.backstory.length > 0
  }
];

const client = generateClient();

const CharacterCreation = () => {
  const navigate = useNavigate();
  const { gameId } = useParams();
  
  // State Management
  const [currentStep, setCurrentStep] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRolling, setIsRolling] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [character, setCharacter] = useState({
    name: '',
    characterClass: '',
    race: '',
    alignment: '',
    abilities: {
      strength: 10,
      dexterity: 10,
      constitution: 10,
      intelligence: 10,
      wisdom: 10,
      charisma: 10
    },
    level: 1,
    experience: 0,
    maxHealth: 10,
    currentHealth: 10,
    gold: 0,
    armorClass: 10,
    speed: 30,
    initiative: 0,
    proficiencyBonus: 2,
    backstory: ''
  });

  // Memoized Character Update Function
  const updateCharacter = useCallback((updates) => {
    setCharacter(prev => ({
      ...prev,
      ...updates
    }));
  }, []);

  // Memoized Handler Functions
  const handleBasicFieldChange = useCallback((field) => (e) => {
    updateCharacter({ [field]: e.target.value });
  }, [updateCharacter]);

  const handleAbilityChange = useCallback((ability, value) => {
    updateCharacter({
      abilities: {
        ...character.abilities,
        [ability]: value
      }
    });
  }, [character.abilities, updateCharacter]);

  // Generation Handlers
  const generateName = useCallback(async () => {
    if (!character.race || !character.characterClass || !character.alignment) {
      setError('Please select race, class, and alignment first');
      return;
    }

    setIsGenerating(true);
    setError('');

    try {
      const response = await client.graphql({
        query: NAME_GENERATION_QUERY,
        variables: {
          input: {
            characterRace: character.race,
            characterClass: character.characterClass,
            characterAlignment: character.alignment
          }
        }
      });

      updateCharacter({ name: response.data.generateCharacterName.name });
      setSuccess('A mystical name has been revealed!');
    } catch (err) {
      setError('The naming ritual failed. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }, [character.race, character.characterClass, character.alignment, updateCharacter]);

  const rollAbilities = useCallback(async () => {
    setIsRolling(true);
    
    try {
      const newAbilities = {};
      for (const ability of Object.keys(character.abilities)) {
        await new Promise(resolve => setTimeout(resolve, 200));
        const rolls = Array(4).fill(0).map(() => Math.floor(Math.random() * 6) + 1);
        rolls.sort((a, b) => b - a);
        newAbilities[ability] = rolls.slice(0, 3).reduce((sum, roll) => sum + roll, 0);
      }

      const updatedCharacter = {
        ...character,
        abilities: newAbilities,
        maxHealth: gameRuleService.calculateMaxHealth(
          newAbilities.constitution,
          character.characterClass
        ),
        armorClass: gameRuleService.calculateArmorClass(newAbilities.dexterity),
        initiative: gameRuleService.calculateInitiative(newAbilities.dexterity)
      };
      
      updatedCharacter.currentHealth = updatedCharacter.maxHealth;
      
      setCharacter(updatedCharacter);
      setSuccess('The dice have spoken!');
    } catch (err) {
      setError('The mystical dice failed to roll. Please try again.');
    } finally {
      setIsRolling(false);
    }
  }, [character, setCharacter]);

  const generateBackstory = useCallback(async () => {
    if (!character.name || !character.race || !character.characterClass || !character.alignment) {
      setError('Please complete your character details first');
      return;
    }

    setIsGenerating(true);
    setError('');

    try {
      const response = await client.graphql({
        query: BACKSTORY_GENERATION_QUERY,
        variables: {
          input: {
            characterName: character.name,
            characterRace: character.race,
            characterClass: character.characterClass,
            characterAlignment: character.alignment
          }
        }
      });

      updateCharacter({ backstory: response.data.generateBackstory.narrative });
      setSuccess('Your tale has been woven!');
    } catch (err) {
      setError('The story weaving failed. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }, [character.name, character.race, character.characterClass, character.alignment, updateCharacter]);

  // Memoized Step Components
  const BasicsStep = useMemo(() => {
    return (
      <motion.div
        className="character-creation__content"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
      >
        <div className="character-creation__form-group">
          <label className="character-creation__label">Mystical Name</label>
          <div className="character-creation__input-group">
            <input
              type="text"
              className="character-creation__input"
              value={character.name}
              onChange={handleBasicFieldChange('name')}
              placeholder="Enter your character's name"
            />
            <button
              className="character-creation__button character-creation__button--secondary"
              onClick={generateName}
              disabled={isGenerating}
            >
              {isGenerating ? (
                <RefreshCw className="spin-animation" />
              ) : (
                <Sparkles />
              )}
              {isGenerating ? 'Channeling...' : 'Generate'}
            </button>
          </div>
        </div>

        <div className="character-creation__form-group">
          <label className="character-creation__label">Ancestral Race</label>
          <select
            className="character-creation__select"
            value={character.race}
            onChange={handleBasicFieldChange('race')}
          >
            <option value="">Choose your race</option>
            {gameRuleService.getRaces().map(race => (
              <option key={race.name} value={race.name}>{race.name}</option>
            ))}
          </select>
          {character.race && (
            <div className="character-creation__description">
              {gameRuleService.getRaceDescription(character.race)}
            </div>
          )}
        </div>

        <div className="character-creation__form-group">
          <label className="character-creation__label">Mystic Class</label>
          <select
            className="character-creation__select"
            value={character.characterClass}
            onChange={handleBasicFieldChange('characterClass')}
          >
            <option value="">Choose your class</option>
            {gameRuleService.getClasses().map(characterClass => (
              <option key={characterClass.name} value={characterClass.name}>
                {characterClass.name}
              </option>
            ))}
          </select>
          {character.characterClass && (
            <div className="character-creation__description">
              {gameRuleService.getClassDescription(character.characterClass)}
            </div>
          )}
        </div>

        <div className="character-creation__form-group">
          <label className="character-creation__label">Moral Alignment</label>
          <select
            className="character-creation__select"
            value={character.alignment}
            onChange={handleBasicFieldChange('alignment')}
          >
            <option value="">Choose your alignment</option>
            {gameRuleService.getAlignments().map(alignment => (
              <option key={alignment.name} value={alignment.name}>
                {alignment.name}
              </option>
            ))}
          </select>
          {character.alignment && (
            <div className="character-creation__description">
              {gameRuleService.getAlignmentDescription(character.alignment)}
            </div>
          )}
        </div>
      </motion.div>
    );
  }, [character.name, character.race, character.characterClass, character.alignment, 
      handleBasicFieldChange, generateName, isGenerating]);

  const AbilitiesStep = useMemo(() => {
    return (
      <motion.div
        className="character-creation__content"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
      >
        <div className="character-creation__abilities">
          {Object.entries(character.abilities).map(([ability, value]) => (
            <motion.div
              key={ability}
              className="character-creation__ability"
              whileHover={{ scale: 1.05 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Object.keys(character.abilities).indexOf(ability) * 0.1 }}
            >
              <div className="character-creation__ability-name">{ability.toUpperCase()}</div>
              <div className="character-creation__ability-score">
                {isRolling ? <Dices className="spin-animation" /> : value}
              </div>
              <div className="character-creation__ability-modifier">
                {!isRolling && (
                  <>
                    {gameRuleService.calculateModifier(value) >= 0 ? '+' : ''}
                    {gameRuleService.calculateModifier(value)}
                  </>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        <button
          className="character-creation__button"
          onClick={rollAbilities}
          disabled={isRolling}
        >
          {isRolling ? (
            <>
              <RefreshCw className="spin-animation" />
              Channeling Power...
            </>
          ) : (
            <>
              <Dices />
              Roll Abilities
            </>
          )}
        </button>

        {character.characterClass && (
          <div className="character-creation__help-text">
            <Info size={20} />
            <p>
              For a {character.characterClass}, focus on{' '}
              {gameRuleService.getSuggestedAbilities(character.characterClass).join(' and ')}
            </p>
          </div>
        )}
      </motion.div>
    );
  }, [character.abilities, character.characterClass, rollAbilities, isRolling]);

  const BackgroundStep = useMemo(() => {
    return (
      <motion.div
        className="character-creation__content"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
      >
        <div className="character-creation__form-group">
          <label className="character-creation__label">Your Legend</label>
          <div className="character-creation__input-group">
            <textarea
              className="character-creation__textarea"
              value={character.backstory}
              onChange={handleBasicFieldChange('backstory')}
              placeholder="Write your character's tale..."
              rows={8}
            />
            <button
              className="character-creation__button character-creation__button--secondary"
              onClick={generateBackstory}
              disabled={isGenerating}
            >
              {isGenerating ? (
                <>
                  <RefreshCw className="spin-animation" />
                  Weaving Tale...
                </>
              ) : (
                <>
                  <Feather />
                  Generate Story
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    );
  }, [character.backstory, handleBasicFieldChange, generateBackstory, isGenerating]);

  // Navigation Handlers
  const handleStepChange = useCallback((index) => {
    if (index < currentStep || CREATION_STEPS[currentStep].validateFn(character)) {
      setCurrentStep(index);
      setError('');
    } else {
      setError('Please complete all required fields before proceeding');
    }
  }, [currentStep, character]);

  const handleComplete = useCallback(async () => {
    if (!CREATION_STEPS[currentStep].validateFn(character)) {
      setError('Please complete all required fields before proceeding');
      return;
    }

    if (currentStep === CREATION_STEPS.length - 1) {
      try {
        setIsGenerating(true);

        // Calculate initial stats
        const constitution = character.abilities.constitution;
        const dexterity = character.abilities.dexterity;
        
        const characterInput = {
          name: character.name,
          characterClass: character.characterClass,
          race: character.race,
          level: 1,
          experience: 0,
          maxHealth: gameRuleService.calculateMaxHealth(
            constitution, 
            character.characterClass
          ),
          currentHealth: gameRuleService.calculateMaxHealth(
            constitution, 
            character.characterClass
          ),
          gold: 0,
          strength: character.abilities.strength,
          dexterity: character.abilities.dexterity,
          constitution: character.abilities.constitution,
          intelligence: character.abilities.intelligence,
          wisdom: character.abilities.wisdom,
          charisma: character.abilities.charisma,
          armorClass: gameRuleService.calculateArmorClass(dexterity),
          speed: 30,
          initiative: gameRuleService.calculateInitiative(dexterity),
          proficiencyBonus: 2,
          alignment: character.alignment,
          backstory: character.backstory,
          notes: ''
        };

        console.log('Creating character with input:', characterInput);

        const response = await client.graphql({
          query: createCharacter,
          variables: {
            input: characterInput
          }
        });

        setSuccess('Your character has been forged!');
        setTimeout(() => {
          navigate(gameId ? `/games/${gameId}/play` : '/characters');
        }, 1500);
      } catch (err) {
        console.error('Error creating character:', err);
        if (err.message.includes('not authenticated')) {
          setError('Please sign in to create a character.');
        } else if (err.message.includes('validation')) {
          setError('Please check all character details are correct.');
        } else {
          setError(`Failed to forge your character. Please try again.`);
        }
      } finally {
        setIsGenerating(false);
      }
    } else {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, character, navigate, gameId]);

  // Memoized UI Components
  const CreationHeader = useMemo(() => (
    <header className="character-creation__header">
      <motion.h1
        className="character-creation__title"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        Forge Your Legend
      </motion.h1>
      <p className="character-creation__subtitle">
        Create a character to begin your adventure
      </p>
    </header>
  ), []);

  const StepsNavigation = useMemo(() => (
    <nav className="character-creation__steps">
      {CREATION_STEPS.map((step, index) => (
        <motion.div
          key={step.id}
          className={`character-creation__step 
            ${currentStep === index ? 'character-creation__step--active' : ''} 
            ${step.validateFn(character) ? 'character-creation__step--completed' : ''}`}
          onClick={() => handleStepChange(index)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.2 }}
        >
          <div className="character-creation__step-icon">
            <step.icon size={28} />
          </div>
          <div className="character-creation__step-title">
            {step.title}
          </div>
        </motion.div>
      ))}
    </nav>
  ), [currentStep, character, handleStepChange]);

  const CharacterPreview = useMemo(() => {
    const stats = {
      health: gameRuleService.calculateMaxHealth(
        character.abilities.constitution,
        character.characterClass
      ),
      armor: gameRuleService.calculateArmorClass(character.abilities.dexterity),
      attack: gameRuleService.calculateModifier(character.abilities.strength) + 
        character.proficiencyBonus
    };

    return (
      <div className="character-creation__preview">
        <div className="character-creation__preview-portrait">
          <motion.div
            className="character-creation__preview-frame"
            animate={{
              boxShadow: [
                "0 0 10px var(--color-rune-glow)",
                "0 0 20px var(--color-rune-glow)",
                "0 0 10px var(--color-rune-glow)"
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <User size={64} />
          </motion.div>
        </div>

        <div className="character-creation__preview-content">
          <h2>{character.name || "Unnamed Hero"}</h2>
          <p className="character-creation__preview-subtitle">
            {[character.race, character.characterClass, character.alignment]
              .filter(Boolean)
              .join(" • ")}
          </p>

          <div className="character-creation__preview-stats">
            <div className="character-creation__stat">
              <Heart className="character-creation__stat-icon" />
              <div className="character-creation__stat-value">{stats.health}</div>
              <div className="character-creation__stat-label">Health</div>
            </div>
            <div className="character-creation__stat">
              <Shield className="character-creation__stat-icon" />
              <div className="character-creation__stat-value">{stats.armor}</div>
              <div className="character-creation__stat-label">Armor</div>
            </div>
            <div className="character-creation__stat">
              <Sword className="character-creation__stat-icon" />
              <div className="character-creation__stat-value">
                {stats.attack >= 0 ? `+${stats.attack}` : stats.attack}
              </div>
              <div className="character-creation__stat-label">Attack</div>
            </div>
          </div>
        </div>
      </div>
    );
  }, [character]);

  const MessageDisplay = useMemo(() => {
    if (!error && !success) return null;
    
    return (
      <>
        {error && (
          <motion.div
            className="character-creation__message character-creation__message--error"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <AlertCircle size={20} />
            <span>{error}</span>
          </motion.div>
        )}
        {success && (
          <motion.div
            className="character-creation__message character-creation__message--success"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Star size={20} />
            <span>{success}</span>
          </motion.div>
        )}
      </>
    );
  }, [error, success]);

  return (
    <div className="character-creation">
      {/* Header */}
      <header className="character-creation__header">
        <motion.h1
          className="character-creation__title"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Forge Your Legend
        </motion.h1>
        <p className="character-creation__subtitle">
          Create a character to begin your adventure
        </p>
      </header>
  
      {/* Steps Navigation */}
      <nav className="character-creation__steps">
        {CREATION_STEPS.map((step, index) => (
          <motion.div
            key={step.id}
            className={`character-creation__step 
              ${currentStep === index ? 'character-creation__step--active' : ''} 
              ${step.validateFn(character) ? 'character-creation__step--completed' : ''}`}
            onClick={() => handleStepChange(index)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.2 }}
          >
            <div className="character-creation__step-icon">
              <step.icon size={28} />
            </div>
            <div className="character-creation__step-title">
              {step.title}
            </div>
          </motion.div>
        ))}
      </nav>
  
      {/* Messages */}
      {(error || success) && (
        <>
          {error && (
            <motion.div
              className="character-creation__message character-creation__message--error"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <AlertCircle size={20} />
              <span>{error}</span>
            </motion.div>
          )}
          {success && (
            <motion.div
              className="character-creation__message character-creation__message--success"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Star size={20} />
              <span>{success}</span>
            </motion.div>
          )}
        </>
      )}
  
      {/* Steps Content */}
      <AnimatePresence mode="wait">
        {currentStep === 0 && BasicsStep}
        {currentStep === 1 && AbilitiesStep}
        {currentStep === 2 && BackgroundStep}
      </AnimatePresence>
  
      {/* Character Preview */}
      <div className="character-creation__preview">
        {/* Character Preview Content */}
        <div className="character-creation__preview-portrait">
          <motion.div
            className="character-creation__preview-frame"
            animate={{
              boxShadow: [
                "0 0 10px var(--color-rune-glow)",
                "0 0 20px var(--color-rune-glow)",
                "0 0 10px var(--color-rune-glow)"
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <User size={64} />
          </motion.div>
        </div>
  
        <div className="character-creation__preview-content">
          <h2>{character.name || "Unnamed Hero"}</h2>
          <p className="character-creation__preview-subtitle">
            {[character.race, character.characterClass, character.alignment]
              .filter(Boolean)
              .join(" • ")}
          </p>
  
          <div className="character-creation__preview-stats">
            <div className="character-creation__stat">
              <Heart className="character-creation__stat-icon" />
              <div className="character-creation__stat-value">
                {gameRuleService.calculateMaxHealth(
                  character.abilities.constitution,
                  character.characterClass
                )}
              </div>
              <div className="character-creation__stat-label">Health</div>
            </div>
            <div className="character-creation__stat">
              <Shield className="character-creation__stat-icon" />
              <div className="character-creation__stat-value">
                {gameRuleService.calculateArmorClass(character.abilities.dexterity)}
              </div>
              <div className="character-creation__stat-label">Armor</div>
            </div>
            <div className="character-creation__stat">
              <Sword className="character-creation__stat-icon" />
              <div className="character-creation__stat-value">
                {gameRuleService.calculateModifier(character.abilities.strength) + 
                  character.proficiencyBonus >= 0 ? '+' : ''}
                {gameRuleService.calculateModifier(character.abilities.strength) + 
                  character.proficiencyBonus}
              </div>
              <div className="character-creation__stat-label">Attack</div>
            </div>
          </div>
        </div>
      </div>
  
      {/* Controls */}
      <div className="character-creation__controls">
        {currentStep > 0 && (
          <button
            className="character-creation__button character-creation__button--secondary"
            onClick={() => setCurrentStep(prev => prev - 1)}
          >
            <ChevronLeft />
            Previous Step
          </button>
        )}
  
        <button
          className="character-creation__button"
          onClick={handleComplete}
          disabled={!CREATION_STEPS[currentStep].validateFn(character)}
        >
          {currentStep === CREATION_STEPS.length - 1 ? (
            <>
              <Star />
              Forge Character
            </>
          ) : (
            <>
              Next Step
              <ChevronRight />
            </>
          )}
        </button>
      </div>
  
      <div className="character-creation__magical-effect character-creation__magical-effect--top" />
      <div className="character-creation__magical-effect character-creation__magical-effect--bottom" />
    </div>
  );
};

export default CharacterCreation;